/**
 * Button — canonical design-system button.
 *
 * One primitive for action, toolbar, inline, and icon-only controls.
 */
import { chakra, Spinner } from "@chakra-ui/react";
import {
  forwardRef,
  useId,
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type ReactNode,
} from "react";

const dsTag = (component: string) => ({ "data-ds": component });

export type ButtonVariant = "solid" | "outline" | "subtle";
export type ButtonTone =
  | "primary"
  | "secondary"
  | "neutral"
  | "success"
  | "error"
  | "warning"
  | "luna";
export type ButtonSize = "xs" | "sm" | "md";

export interface ButtonProps extends Omit<
  ComponentPropsWithoutRef<"button">,
  "aria-label" | "children" | "color" | "onClick"
> {
  variant?: ButtonVariant;
  tone?: ButtonTone;
  size?: ButtonSize;
  /** Pin a visual state for previews; runtime usually omits this. */
  forceState?: "default" | "hover" | "disabled";
  isDisabled?: boolean;
  /** Shows a spinner and blocks interaction while keeping the button tone. */
  isLoading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  /** Renders a square icon-only button. Pair with `aria-label`. */
  icon?: ReactNode;
  /** Accessible label, required for icon-only buttons. */
  "aria-label"?: string;
  children?: ReactNode;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
}

const LUNA_GRADIENT = {
  solid: {
    image: "var(--chakra-colors-bg-lunaSolidGradient)",
    size: "200% 100%, 200% 100%",
    clip: "padding-box, border-box",
  },
  outline: {
    image: "var(--chakra-colors-bg-lunaOutlineGradient)",
    size: "100% 100%, 200% 100%",
    clip: "padding-box, border-box",
  },
  subtle: {
    image: "var(--chakra-colors-bg-lunaGradient)",
    size: "200% 100%",
    clip: "text",
  },
} as const;

const LUNA_POS = {
  solid: ["0% 0, 0% 0", "100% 0, 100% 0"],
  outline: ["0% 0, 0% 0", "0% 0, 100% 0"],
  subtle: ["0% 0", "100% 0"],
} as const;

const LUNA_TEXT_SX = {
  backgroundImage:
    "linear-gradient(90deg, #5861DE 0%, #ED6F6F 50%, #5861DE 100%)",
  backgroundSize: "200% 100%",
  backgroundPosition: "var(--luna-flip, 0%) 0",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  transition: "background-position 400ms cubic-bezier(0.37, 0, 0.63, 1)",
} as const;

const hoverWash = (topVar: string) =>
  `linear-gradient(to top, var(--chakra-colors-neutral-0) 0%, var(${topVar}) 100%)`;

const OUTLINE_CHIP: Record<
  "primary" | "secondary" | "success" | "error" | "warning",
  { color: string; shadow: string; hoverImage: string }
> = {
  primary: {
    color: "btn.outline.primary-text",
    shadow: "outline-btn-primary",
    hoverImage: hoverWash("--chakra-colors-p-50"),
  },
  secondary: {
    color: "btn.outline.secondary-text",
    shadow: "outline-btn",
    hoverImage: hoverWash("--chakra-colors-neutral-100"),
  },
  success: {
    color: "btn.outline.success-text",
    shadow: "outline-btn-success",
    hoverImage: hoverWash("--chakra-colors-success-50"),
  },
  error: {
    color: "btn.outline.error-text",
    shadow: "outline-btn-error",
    hoverImage: hoverWash("--chakra-colors-error-50"),
  },
  warning: {
    color: "btn.outline.warning-text",
    shadow: "outline-btn-warning",
    hoverImage: hoverWash("--chakra-colors-warning-50"),
  },
};

type Style = Record<string, unknown>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "solid",
      tone = "primary",
      size = "md",
      forceState = "default",
      isDisabled,
      isLoading,
      leadingIcon,
      trailingIcon,
      icon,
      "aria-label": ariaLabel,
      children,
      disabled: nativeDisabled,
      onClick,
      type = "button",
      ...rest
    },
    ref,
  ) => {
    const loading = Boolean(isLoading) && forceState !== "disabled";
    const disabled =
      (Boolean(isDisabled) ||
        Boolean(nativeDisabled) ||
        forceState === "disabled") &&
      !loading;
    const iconOnly = icon != null || children == null;
    const lunaSubtle = variant === "subtle" && tone === "luna";
    const isLuna = tone === "luna";
    const lunaInk = isLuna && variant !== "solid" && !disabled;
    const lunaSolid = isLuna && variant === "solid" && !disabled;
    const iconGradId = "lunaGrad" + useId().replace(/[^a-zA-Z0-9]/g, "");

    const lunaInkSx: Style = lunaInk
      ? {
          "--luna-flip": "0%",
          "--luna-stop-a": "#5861DE",
          "--luna-stop-b": "#ED6F6F",
          "& svg[stroke='currentColor']": { stroke: `url(#${iconGradId})` },
          "& stop": {
            transition: "stop-color 400ms cubic-bezier(0.37, 0, 0.63, 1)",
          },
          "&:hover, &[data-luna-hover]": {
            "--luna-flip": "100%",
            "--luna-stop-a": "#ED6F6F",
            "--luna-stop-b": "#5861DE",
          },
        }
      : {};

    const lunaStyles: Style = isLuna
      ? {
          ...(variant === "solid"
            ? { color: "neutral.0", boxShadow: "solid-btn" }
            : { color: "#5861DE" }),
        }
      : {};

    const lunaBgSx =
      isLuna && !disabled && variant === "solid"
        ? {
            backgroundImage: LUNA_GRADIENT[variant].image,
            backgroundSize: LUNA_GRADIENT[variant].size,
            backgroundOrigin: "padding-box, border-box",
            backgroundClip: LUNA_GRADIENT[variant].clip,
            backgroundRepeat: "no-repeat",
          }
        : {};

    const dim = size === "xs" ? "20px" : size === "sm" ? "24px" : "32px";
    const sizeStyles: Style =
      size === "xs"
        ? {
            h: dim,
            minW: dim,
            px: "2xs",
            borderRadius: "component.sm",
            fontSize: "2xs",
          }
        : size === "sm"
          ? {
              h: dim,
              minW: dim,
              px: "1xs",
              borderRadius: "component.md",
              fontSize: "xs",
            }
          : {
              h: dim,
              minW: dim,
              px: "sm",
              borderRadius: "component.lg",
              fontSize: "md",
            };

    const variantStyles = (hover: boolean): Style => {
      if (variant === "solid") {
        if (tone === "luna") {
          return { backgroundPosition: LUNA_POS.solid[hover ? 1 : 0] };
        }
        if (tone === "neutral") {
          return {
            bg: hover ? "bg.app" : "bg.component",
            color: "text.body.primary",
            boxShadow: hover ? "elevHigh" : "elevLow",
          };
        }
        return {
          bg: hover ? `btn.solid.${tone}-hover` : `btn.solid.${tone}-bg`,
          color: `btn.solid.${tone}-text`,
          borderColor: hover
            ? `btn.solid.${tone}-border-hover`
            : `btn.solid.${tone}-border`,
          boxShadow: "solid-btn",
        };
      }

      if (variant === "outline") {
        if (tone === "neutral") {
          return {
            borderColor: hover ? "border.hover" : "border.default",
            color: "text.body.primary",
            bg: hover ? "bg.hover" : "transparent",
          };
        }
        if (tone === "luna") {
          return hover
            ? {
                bg: "bg.input",
                backgroundImage: hoverWash("--chakra-colors-luna-50"),
                boxShadow: "outline-btn-luna",
                border: "none",
              }
            : {
                bg: "bg.input",
                boxShadow: "outline-btn-luna",
                border: "none",
              };
        }
        const chip = OUTLINE_CHIP[tone];
        if (hover) {
          return {
            bg: "bg.input",
            backgroundImage: chip.hoverImage,
            color: chip.color,
            boxShadow: chip.shadow,
            border: "none",
          };
        }
        return {
          bg: "bg.input",
          color: chip.color,
          boxShadow: chip.shadow,
          border: "none",
        };
      }

      if (tone === "luna") {
        return { bg: hover ? "btn.subtle.luna-hover" : "transparent" };
      }
      if (tone === "neutral") {
        return {
          color: "text.body.primary",
          bg: hover ? "bg.hover" : "transparent",
        };
      }
      return {
        color: `btn.subtle.${tone}-text`,
        bg: hover ? `btn.subtle.${tone}-hover` : "transparent",
      };
    };

    const disabledStyles: Style =
      variant === "solid"
        ? {
            bg: "bg.disabled",
            color: "text.disabled.default",
            boxShadow: "none",
            borderColor: "transparent",
          }
        : variant === "outline"
          ? { borderColor: "border.default", color: "text.disabled.default" }
          : { color: "text.disabled.default" };

    const base: Style = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "1xs",
      fontFamily: "body",
      fontWeight: 600,
      lineHeight: "1",
      border: "1px solid transparent",
      bg: isLuna ? undefined : "transparent",
      whiteSpace: "nowrap",
      transition: isLuna
        ? "background-position 400ms cubic-bezier(0.37, 0, 0.63, 1), background-color 200ms ease, box-shadow 200ms ease, color 200ms ease"
        : "background 120ms, box-shadow 120ms, border-color 120ms, color 120ms",
      cursor: disabled ? "not-allowed" : loading ? "progress" : "pointer",
      ...sizeStyles,
      ...(iconOnly ? { w: dim, px: "0" } : {}),
      ...lunaStyles,
    };

    const applied: Style = disabled
      ? disabledStyles
      : forceState === "hover"
        ? variantStyles(true)
        : variantStyles(false);
    const hoverStyles: Style | undefined =
      !disabled && !loading && forceState === "default"
        ? variantStyles(true)
        : undefined;

    const spinner = (
      <Spinner
        size={size === "md" ? "sm" : "xs"}
        color={lunaSubtle ? "luna.0" : "currentColor"}
        thickness="2px"
        speed="0.65s"
        sx={lunaSubtle ? { WebkitTextFillColor: "#5861DE" } : undefined}
      />
    );

    return (
      <chakra.button
        ref={ref}
        {...dsTag("Button")}
        type={type}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-busy={loading || undefined}
        data-luna-hover={lunaInk && forceState === "hover" ? "" : undefined}
        onClick={disabled || loading ? undefined : onClick}
        sx={{
          "& svg": { display: "block" },
          ...(lunaSolid
            ? {
                "& svg": {
                  display: "block",
                  filter: "brightness(0) invert(1)",
                },
              }
            : {}),
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
            "& *": { transition: "none" },
          },
          ...lunaBgSx,
          ...lunaInkSx,
        }}
        _hover={hoverStyles}
        _focusVisible={{
          outline: "none",
          boxShadow:
            variant === "solid" && tone !== "neutral"
              ? "0 0 0 3px rgba(88,97,222,0.25), 0 1px 3px rgba(30,90,180,0.35), 0 2px 6px rgba(30,90,180,0.20), inset 0 1px 0 rgba(255,255,255,0.18)"
              : "0 0 0 3px rgba(88,97,222,0.25)",
        }}
        {...base}
        {...applied}
        {...rest}
      >
        {lunaInk && (
          <svg
            width="0"
            height="0"
            aria-hidden
            style={{ position: "absolute" }}
          >
            <linearGradient id={iconGradId} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="var(--luna-stop-a)" />
              <stop offset="100%" stopColor="var(--luna-stop-b)" />
            </linearGradient>
          </svg>
        )}
        {icon != null ? (
          loading ? (
            spinner
          ) : (
            icon
          )
        ) : (
          <>
            {loading ? spinner : leadingIcon}
            {lunaInk && children != null ? (
              <chakra.span sx={LUNA_TEXT_SX}>{children}</chakra.span>
            ) : (
              children
            )}
            {!loading && trailingIcon}
          </>
        )}
      </chakra.button>
    );
  },
);

Button.displayName = "Button";

export default Button;
